import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';
import dayjs from 'dayjs';
import { FaultCodes } from '@feedflo/fault-codes';

import { BIN_CONTROLLER_SENSOR_STATUS_GQL } from './queries';

const DEFAULT_POLL_INTERVAL_MS = 30_000;

export const UPLOAD_SUCCESS_WINDOW_SECONDS = 300;

// Published @feedflo/fault-codes@1.0.18 predates bin-controller LAN codes; keep literals as fallback.
const LAN_DEVICE_CONNECTED = FaultCodes.LAN_DEVICE_CONNECTED ?? 1103;
const LAN_DEVICE_DISCONNECTED = FaultCodes.LAN_DEVICE_DISCONNECTED ?? 1104;

const LAN_FAULT_NAMES = {
  [LAN_DEVICE_CONNECTED]: 'LAN Device Connected',
  [LAN_DEVICE_DISCONNECTED]: 'LAN Device Disconnected',
};

export function decodeFaultDetails(details) {
  if (!details) {
    return null;
  }
  try {
    return JSON.parse(atob(details));
  } catch {
    return null;
  }
}

export function eventRank(fault, decoded) {
  const boot = Number(decoded?.boot ?? 0);
  const i = Number(decoded?.i ?? 0);
  const startedAt = Number(fault.started_at ?? 0);
  return boot * 1e12 + i * 1e6 + startedAt;
}

function normalizeFaultCode(code) {
  return Number(code);
}

function lanFaultName(code) {
  const normalized = normalizeFaultCode(code);
  return LAN_FAULT_NAMES[normalized] ?? `Unknown (${code})`;
}

export function getUploadStatus(lastTransaction, nowUnix) {
  if (!lastTransaction?.occured_at) {
    return {
      lastTransactionAt: null,
      lastTransactionStatus: null,
      uploadStatus: 'unknown',
    };
  }

  const occuredAt = Number(lastTransaction.occured_at);
  const status = lastTransaction.status ?? null;
  const isSuccess = status === '200';
  const isRecent = occuredAt >= nowUnix - UPLOAD_SUCCESS_WINDOW_SECONDS;

  let uploadStatus = 'failed';
  if (isSuccess && isRecent) {
    uploadStatus = 'success';
  } else if (isSuccess) {
    uploadStatus = 'stale';
  }

  return {
    lastTransactionAt: occuredAt,
    lastTransactionStatus: status,
    uploadStatus,
  };
}

function getExpectedDevices(binController) {
  const feedLines = binController?.bin_set?.feed_lines ?? [];
  const devices = [];

  for (const feedLine of feedLines) {
    const assignments = feedLine?.device_assignments ?? [];
    for (const assignment of assignments) {
      const device = assignment?.device;
      if (!device?.serial) {
        continue;
      }
      devices.push({
        serial: device.serial,
        deviceId: device.id ?? null,
        deviceType: device.type ?? null,
        feedLineId: feedLine.id ?? null,
        feedLineName: feedLine.name ?? null,
        lastTransaction: device.lastTransaction?.[0] ?? null,
      });
    }
  }

  const bySerial = new Map();
  for (const device of devices) {
    bySerial.set(device.serial, device);
  }
  return [...bySerial.values()];
}

function filterLanEventsByBootCount(lanEvents, resetBoot) {
  if (resetBoot == null) {
    return lanEvents ?? [];
  }
  return (lanEvents ?? []).filter((fault) => {
    const decoded = decodeFaultDetails(fault.details);
    if (decoded?.boot == null) {
      return false;
    }
    return Number(decoded.boot) >= resetBoot;
  });
}

function latestLanStatusBySerial(lanEvents) {
  const bySerial = new Map();

  for (const fault of lanEvents ?? []) {
    const decoded = decodeFaultDetails(fault.details);
    const serial = decoded?.serial;
    if (!serial) {
      continue;
    }

    const rank = eventRank(fault, decoded);
    const existing = bySerial.get(serial);
    if (existing && existing.rank >= rank) {
      continue;
    }

    const faultCode = normalizeFaultCode(fault.code);
    bySerial.set(serial, {
      serial,
      lanStatus: faultCode === LAN_DEVICE_CONNECTED ? 'connected' : 'disconnected',
      rank,
      lastEvent: {
        id: fault.id ?? null,
        code: faultCode,
        name: lanFaultName(faultCode),
        startedAt: fault.started_at ?? null,
        endedAt: fault.ended_at ?? null,
        boot: decoded?.boot ?? null,
        faultIndex: decoded?.i ?? null,
        reason: decoded?.reason ?? null,
        ip: decoded?.ip ?? null,
      },
    });
  }

  return bySerial;
}

function sortDevices(devices) {
  return [...devices].sort((a, b) => {
    if (a.isUnassigned !== b.isUnassigned) {
      return a.isUnassigned ? 1 : -1;
    }
    const feedLineA = a.feedLineName ?? '';
    const feedLineB = b.feedLineName ?? '';
    if (feedLineA !== feedLineB) {
      return feedLineA.localeCompare(feedLineB);
    }
    return a.serial.localeCompare(b.serial);
  });
}

export function calculateBinControllerSensorStatus(binController, nowUnix) {
  if (!binController) {
    return null;
  }

  const device = binController.device ?? {};
  const lastResetFault = device.last_reset?.[0] ?? null;
  const lastResetAt = lastResetFault?.started_at ?? null;
  const resetBoot = (() => {
    const decoded = decodeFaultDetails(lastResetFault?.details ?? null);
    return decoded?.boot != null ? Number(decoded.boot) : null;
  })();
  const lanEvents = filterLanEventsByBootCount(device.lan_events, resetBoot);
  const lanBySerial = latestLanStatusBySerial(lanEvents);
  const expectedDevices = getExpectedDevices(binController);
  const expectedSerials = new Set(expectedDevices.map((d) => d.serial));

  const assignedDevices = expectedDevices.map((expected) => {
    const latest = lanBySerial.get(expected.serial);
    const upload = getUploadStatus(expected.lastTransaction, nowUnix);

    return {
      serial: expected.serial,
      deviceId: expected.deviceId,
      deviceType: expected.deviceType,
      feedLineId: expected.feedLineId,
      feedLineName: expected.feedLineName,
      isUnassigned: false,
      lanStatus: latest?.lanStatus ?? 'unknown',
      lastEvent: latest?.lastEvent ?? null,
      ...upload,
    };
  });

  const unassignedDevices = [...lanBySerial.values()]
    .filter((entry) => !expectedSerials.has(entry.serial))
    .map((entry) => ({
      serial: entry.serial,
      deviceId: null,
      deviceType: null,
      feedLineId: null,
      feedLineName: 'Unassigned',
      isUnassigned: true,
      lanStatus: entry.lanStatus,
      lastEvent: entry.lastEvent,
      lastTransactionAt: null,
      lastTransactionStatus: null,
      uploadStatus: 'unknown',
    }));

  const devices = sortDevices([...assignedDevices, ...unassignedDevices]);

  return {
    controller: {
      binControllerId: binController.id ?? null,
      deviceId: binController.device_id ?? null,
      serial: device.serial ?? null,
      lastResetAt,
    },
    summary: {
      expectedDeviceCount: expectedDevices.length,
      connectedCount: devices.filter((d) => d.lanStatus === 'connected').length,
      disconnectedCount: devices.filter((d) => d.lanStatus === 'disconnected').length,
      unknownCount: devices.filter((d) => d.lanStatus === 'unknown').length,
      unassignedCount: unassignedDevices.length,
    },
    devices,
  };
}

export default function useBinControllerSensorStatus({
  deviceId,
  skip = false,
  pollInterval = DEFAULT_POLL_INTERVAL_MS,
}) {
  const { loading, error, data, refetch } = useQuery(BIN_CONTROLLER_SENSOR_STATUS_GQL, {
    variables: { deviceId },
    skip: skip || !deviceId,
    pollInterval: skip ? 0 : pollInterval,
    fetchPolicy: 'network-only',
    context: {
      headers: {
        'x-hasura-role': 'user',
      },
    },
    onError: (queryError) => {
      console.error('Failed to fetch bin controller sensor status:', queryError);
      Sentry.captureException(queryError);
    },
  });

  const result = useMemo(() => {
    const binController = data?.bin_controller?.[0];
    if (!binController) {
      return null;
    }
    return calculateBinControllerSensorStatus(binController, dayjs.tz().unix());
  }, [data]);

  return {
    loading,
    error,
    refetch,
    controller: result?.controller ?? null,
    devices: result?.devices ?? [],
    summary: result?.summary ?? null,
  };
}
